import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Settings, Plus, Edit, Trash2, FileText } from 'lucide-react';

export default function AjustementsTab({
  ajustementsMontants,
  setEditingAjustement,
  resetAjustementForm,
  setAjustementDialogOpen,
  openEditAjustement,
  handleDeleteAjustement,
}) {
  return (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-white">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Settings className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Ajustements des Montants</h2>
                      <p className="text-purple-100">Personnalisez les montants de base, Global et CA affichés dans le simulateur</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => {
                      setEditingAjustement(null);
                      resetAjustementForm();
                      setAjustementDialogOpen(true);
                    }}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvel Ajustement
                  </Button>
                </div>
              </div>
              
              <CardContent className="p-6">
                {ajustementsMontants.length === 0 ? (
                  <div className="text-center py-12">
                    <Settings className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600">Aucun ajustement configuré</h3>
                    <p className="text-slate-500 mt-2">
                      Créez un ajustement pour personnaliser les montants du simulateur BATICO
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ajustementsMontants.map((ajustement) => (
                      <Card key={ajustement.id} className={`border-2 ${ajustement.is_active ? 'border-purple-200 bg-purple-50' : 'border-slate-200 bg-slate-50'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${ajustement.is_active ? 'bg-purple-600' : 'bg-slate-400'}`}>
                                {ajustement.type_ajustement === 'remplacement' && <span className="text-white text-xl">🔄</span>}
                                {ajustement.type_ajustement === 'pourcentage' && <span className="text-white text-xl">📊</span>}
                                {ajustement.type_ajustement === 'fixe' && <span className="text-white text-xl">💰</span>}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-slate-800">{ajustement.nom}</h4>
                                  <Badge className={ajustement.is_active ? 'bg-green-600' : 'bg-slate-400'}>
                                    {ajustement.is_active ? 'Actif' : 'Inactif'}
                                  </Badge>
                                  {ajustement.afficher_sur_documents && (
                                    <Badge className="bg-blue-600 text-white font-semibold">
                                      <FileText className="h-3 w-3 mr-1" />
                                      Documents
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500">{ajustement.description}</p>
                                
                                {/* Afficher les valeurs de remplacement */}
                                {ajustement.type_ajustement === 'remplacement' && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {ajustement.valeur_remplacement_ht && (
                                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                        HT: {parseFloat(ajustement.valeur_remplacement_ht).toLocaleString('fr-FR')}€
                                      </span>
                                    )}
                                    {ajustement.valeur_remplacement_ttc && (
                                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                        TTC: {parseFloat(ajustement.valeur_remplacement_ttc).toLocaleString('fr-FR')}€
                                      </span>
                                    )}
                                    {ajustement.valeur_remplacement_ca && (
                                      <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full">
                                        CA: {parseFloat(ajustement.valeur_remplacement_ca).toLocaleString('fr-FR')}€
                                      </span>
                                    )}
                                  </div>
                                )}
                                
                                {/* Afficher la valeur pour pourcentage ou fixe */}
                                {ajustement.type_ajustement === 'pourcentage' && (
                                  <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full mt-2 inline-block">
                                    {ajustement.valeur > 0 ? '+' : ''}{ajustement.valeur}%
                                  </span>
                                )}
                                {ajustement.type_ajustement === 'fixe' && (
                                  <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full mt-2 inline-block">
                                    {ajustement.valeur > 0 ? '+' : ''}{parseFloat(ajustement.valeur).toLocaleString('fr-FR')}€
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openEditAjustement(ajustement)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleDeleteAjustement(ajustement.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
  );
}
